// LAS COLUMNAS DE PÓLIZAS ACTIVAS DEL EXCEL DE CLIENTES.
//
// Lissette, 2026-10-08: en la exportación de todos los clientes, de las
// pólizas ACTIVAS tiene que salir la prima, cuándo vencen, la forma de pago y
// los costos del plan (deducible, máximo de bolsillo, doctor primario y sala
// de emergencia). El resumen "Polizas Detalle" de siempre se queda como está:
// esto se AÑADE en columnas propias para poder filtrar y ordenar en Excel.
//
// Una fila del Excel es un cliente, y un cliente puede tener varias pólizas
// activas. Cada columna junta los valores con " | " en el MISMO orden en todas
// las columnas, así la tercera prima es la de la tercera póliza. Cuando hay
// más de una, cada valor lleva delante la compañía para saber de cuál es.
//
// Acepta las dos formas en que llega una póliza: la del servidor
// (`POST /clients/export`, estatus 'ACTIVE', `expirationDate`, `premium`) y la
// de la pantalla (estatus 'active', `renewalDate`, `monthlyPremium`).
//
// Vive fuera de deskte-full.jsx para poder probarse sin montar React.

const FORMA = {
  autopay: ['Autopay', 'Autopay'],
  client: ['Cliente paga', 'Client pays'],
  force: ['Forzar', 'Force'],
};
const METODO = {
  credit_card: ['Tarjeta de Credito', 'Credit Card'],
  debit_card: ['Tarjeta de Debito', 'Debit Card'],
  bank_account: ['Cuenta Bancaria (ACH)', 'Bank Account (ACH)'],
};

const texto = (v) => (v === null || v === undefined ? '' : String(v).trim());

/** ¿Está activa? Solo el estatus base: una cancelada o pendiente no entra. */
export const esActiva = (p) => texto(p?.status).toLowerCase() === 'active';

/** "2026-12-31" -> "12/31/2026" sin pasar por Date (que correría el día). */
export function fechaEnUS(v) {
  const s = texto(v);
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : s;
}

/** Cifra escrita a mano -> "$2,500"; si no es cifra ("20%"), tal cual. */
function dinero(v) {
  const s = texto(v);
  if (!s) return '';
  if (!/^\$?\s*[\d,]+(\.\d+)?$/.test(s)) return s;
  const n = Number(s.replace(/[$,\s]/g, ''));
  return Number.isFinite(n) ? `$${n.toLocaleString('en-US', { maximumFractionDigits: 2 })}` : s;
}

/** "Autopay - Tarjeta de Credito Visa ****1234" / "Cliente paga". */
export function formaDePago(p, es = true) {
  const i = es ? 0 : 1;
  const forma = FORMA[texto(p.paymentType)]?.[i] || texto(p.paymentType);
  const metodo = METODO[texto(p.paymentMethod)]?.[i] || texto(p.paymentMethod);
  const marca = texto(p.cardBrand);
  const ultimos = texto(p.paymentMethod) === 'bank_account' ? texto(p.bankLast4) : texto(p.cardLast4);
  const detalle = [metodo, marca && marca !== 'other' ? marca.toUpperCase() : '', ultimos ? `****${ultimos}` : '']
    .filter(Boolean).join(' ');
  return [forma, detalle].filter(Boolean).join(' - ');
}

function prima(p) {
  if (p.premium !== undefined && p.premium !== null && p.premium !== '') {
    const f = texto(p.premiumFrequency).toUpperCase();
    const sufijo = !f || f === 'MONTHLY' ? '/mo' : `/${f.toLowerCase()}`;
    return `${dinero(p.premium) || '$0'}${sufijo}`;
  }
  return p.monthlyPremium !== undefined && p.monthlyPremium !== '' ? `${dinero(p.monthlyPremium) || '$0'}/mo` : '';
}

/**
 * Las columnas, listas para mezclar en la fila del cliente.
 * Las cabeceras van sin acentos, como el resto de las del Excel.
 */
export function columnasDePolizasActivas(polizas, es = true) {
  const activas = (Array.isArray(polizas) ? polizas : []).filter(esActiva);
  const varias = activas.length > 1;
  const col = (fn) => activas
    .map((p) => {
      const v = fn(p);
      return varias ? `${texto(p.carrier) || '?'}: ${v || '--'}` : v;
    })
    .join(' | ');
  return {
    [es ? 'Polizas activas' : 'Active policies']: col((p) => [texto(p.carrier), texto(p.plan)].filter(Boolean).join(' - ')),
    [es ? 'Prima (activas)' : 'Premium (active)']: col(prima),
    [es ? 'Vencimiento (activas)' : 'Expiration (active)']: col((p) => fechaEnUS(p.expirationDate || p.renewalDate)),
    [es ? 'Forma de pago (activas)' : 'Payment method (active)']: col((p) => formaDePago(p, es)),
    [es ? 'Deducible (activas)' : 'Deductible (active)']: col((p) => dinero(p.deductible)),
    [es ? 'Max. de bolsillo (activas)' : 'Out-of-pocket max (active)']: col((p) => dinero(p.outOfPocketMax)),
    [es ? 'Doctor primario PCP (activas)' : 'Primary care PCP (active)']: col((p) => texto(p.pcpCopay)),
    [es ? 'Sala de emergencia (activas)' : 'Emergency room (active)']: col((p) => texto(p.erCopay)),
    [es ? 'Especialista (activas)' : 'Specialist (active)']: col((p) => texto(p.specialistCopay)),
    [es ? 'Medicina generica (activas)' : 'Generic drugs (active)']: col((p) => texto(p.genericDrugCopay)),
  };
}

/**
 * UN BLOQUE DE COLUMNAS POR PÓLIZA (Lissette 2026-10-15): "si el cliente tiene
 * varias pólizas y tipos de póliza no se está descargando la información de
 * esas pólizas". Las columnas de arriba juntan las activas en una celda con
 * " | "; aquí cada póliza —activa o no, de cualquier tipo— tiene sus propias
 * columnas: "Poliza 1 - Tipo", "Poliza 1 - Compania", … "Poliza 2 - Tipo"…
 *
 * `cuantas` es el número de pólizas del cliente que más tiene en la
 * exportación: TODAS las filas llevan las mismas columnas (el CSV toma las
 * cabeceras de la primera fila, y una columna que solo existe en la fila 40
 * se perdería).
 */
export function columnasPorPoliza(polizas, cuantas, es = true) {
  const lista = Array.isArray(polizas) ? polizas : [];
  const out = {};
  for (let i = 0; i < cuantas; i++) {
    const p = lista[i];
    const n = `${es ? 'Poliza' : 'Policy'} ${i + 1} - `;
    const v = (fn) => (p ? fn(p) : '');
    out[`${n}${es ? 'Tipo' : 'Type'}`] = v((x) => texto(x.type));
    out[`${n}${es ? 'Compania' : 'Carrier'}`] = v((x) => texto(x.carrier));
    out[`${n}Plan`] = v((x) => texto(x.plan));
    out[`${n}${es ? '# Poliza' : 'Policy #'}`] = v((x) => texto(x.policyNumber));
    out[`${n}Member ID`] = v((x) => texto(x.memberId));
    out[`${n}${es ? 'Estatus' : 'Status'}`] = v((x) => texto(x.status).toLowerCase());
    out[`${n}${es ? 'Prima' : 'Premium'}`] = v(prima);
    out[`${n}${es ? 'Efectiva' : 'Effective'}`] = v((x) => fechaEnUS(x.effectiveDate));
    out[`${n}${es ? 'Vencimiento' : 'Expiration'}`] = v((x) => fechaEnUS(x.expirationDate || x.renewalDate));
    out[`${n}${es ? 'Forma de pago' : 'Payment method'}`] = v((x) => formaDePago(x, es));
    out[`${n}${es ? 'Deducible' : 'Deductible'}`] = v((x) => dinero(x.deductible));
    out[`${n}${es ? 'Max. de bolsillo' : 'Out-of-pocket max'}`] = v((x) => dinero(x.outOfPocketMax));
  }
  return out;
}

/**
 * Una celda lista para Excel. Dos cosas:
 *  - Excel no admite más de 32.767 caracteres en una celda y la librería
 *    LANZA un error: un cliente con notas larguísimas tumbaba la descarga
 *    entera en Excel mientras el CSV salía (Lissette 2026-10-15).
 *  - Los saltos de línea se vuelven " · ": con ellos cada fila salía alta como
 *    un párrafo y no se podían ver los clientes. La nota queda en una línea
 *    larga; al hacer clic en la celda se lee entera arriba, en la barra.
 */
export const MAX_CELDA_EXCEL = 32767;
export function celdaEnUnaLinea(v) {
  if (typeof v !== 'string') return v;
  const plano = v.replace(/\s*[\r\n]+\s*/g, ' · ');
  return plano.length > MAX_CELDA_EXCEL ? `${plano.slice(0, MAX_CELDA_EXCEL - 20)}… [recortado]` : plano;
}

/** El documento migratorio: # USCIS / A, # de tarjeta y vencimiento. */
export function columnasDeDocumento(doc, prefijo, es = true) {
  const d = doc || {};
  return {
    [`${prefijo}${es ? '# USCIS / A' : 'USCIS / A #'}`]: texto(d.uscisNumber),
    [`${prefijo}${es ? '# Tarjeta' : 'Card #'}`]: texto(d.cardNumber),
    [`${prefijo}${es ? 'Vence documento' : 'Document expires'}`]: fechaEnUS(d.expirationDate),
  };
}
